import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LifeBuoy, Send, CheckCircle2, RotateCcw, Building2, Paperclip, FileText } from 'lucide-react';
import {
  listSupportThreads, getSupportMessages, postSupportMessage, setSupportThreadStatus,
  postSupportTyping, uploadSupportAttachment, fetchSupportAttachment,
  SupportThread, SupportMessage,
} from '../services/api';
import { fmtDateTimeJst } from '../utils/datetime';

// 添付ファイル表示（画像はプレビュー、PDF等はダウンロードチップ）
function AttachmentView({ messageId, name, mime }: { messageId: string; name: string; mime: string }) {
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const isImage = mime.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    let url: string | null = null; let cancelled = false;
    fetchSupportAttachment(messageId)
      .then(b => { if (cancelled) return; url = URL.createObjectURL(b); setObjUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [messageId, isImage]);

  const download = async () => {
    try {
      const b = await fetchSupportAttachment(messageId);
      const u = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = u; a.download = name; a.click();
      URL.revokeObjectURL(u);
    } catch { /* ignore */ }
  };

  if (isImage) {
    return objUrl
      ? <img src={objUrl} alt={name} onClick={download} title="クリックで保存"
          style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, cursor: 'pointer', display: 'block', marginTop: 4 }} />
      : <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>画像を読み込み中...</div>;
  }
  return (
    <button onClick={download}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4, padding: '6px 10px', background: 'rgba(255,255,255,0.9)', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#111827', fontWeight: 600, maxWidth: '100%' }}>
      <FileText size={13} color="#dc2626" style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
    </button>
  );
}

// 顧客対応: 各社の管理者からの問い合わせ一覧 + リアルタイムチャット（ポーリング）

const POLL_LIST_MS = 10_000;
const POLL_THREAD_MS = 5_000;

// 日本時刻で表示（保存値はUTC）。
function fmt(dt: string | null | undefined): string {
  return fmtDateTimeJst(dt);
}

export default function SupportPage() {
  const [threads, setThreads]       = useState<SupportThread[]>([]);
  const [activeId, setActiveId]     = useState<string | null>(null);
  const [thread, setThread]         = useState<SupportThread | null>(null);
  const [messages, setMessages]     = useState<SupportMessage[]>([]);
  const [input, setInput]           = useState('');
  const [sending, setSending]       = useState(false);
  const [filter, setFilter]         = useState<'all' | 'open' | 'closed'>('open');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [peerTyping, setPeerTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMsgCount = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastTypingSent = useRef(0);

  const fetchThreads = useCallback(async () => {
    try { setThreads(await listSupportThreads()); } catch { /* ignore */ }
  }, []);

  const fetchMessages = useCallback(async (id: string) => {
    try {
      const d = await getSupportMessages(id);
      setThread(d.thread);
      setMessages(d.messages);
      setPeerTyping(!!d.peer_typing);
    } catch { /* ignore */ }
  }, []);

  // 入力中シグナル（2.5秒に1回に間引いて送信）
  const signalTyping = useCallback((threadId: string) => {
    const now = Date.now();
    if (now - lastTypingSent.current < 2_500) return;
    lastTypingSent.current = now;
    postSupportTyping(threadId).catch(() => {});
  }, []);

  // 添付ファイル送信
  const uploadFile = async (file: File) => {
    if (!activeId || sending) return;
    setSending(true);
    try {
      await uploadSupportAttachment(activeId, file);
      await fetchMessages(activeId);
      fetchThreads();
    } catch { /* ignore */ }
    setSending(false);
  };

  // 一覧ポーリング
  useEffect(() => {
    fetchThreads();
    const t = setInterval(fetchThreads, POLL_LIST_MS);
    return () => clearInterval(t);
  }, [fetchThreads]);

  // 選択スレッドのポーリング
  useEffect(() => {
    if (!activeId) return;
    fetchMessages(activeId);
    const t = setInterval(() => fetchMessages(activeId), POLL_THREAD_MS);
    return () => clearInterval(t);
  }, [activeId, fetchMessages]);

  // 新着時だけ最下部へスクロール
  useEffect(() => {
    if (messages.length !== prevMsgCount.current) {
      prevMsgCount.current = messages.length;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const send = async () => {
    const body = input.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    try {
      await postSupportMessage(activeId, body);
      setInput('');
      await fetchMessages(activeId);
      fetchThreads();
    } catch { /* ignore */ }
    setSending(false);
  };

  const toggleStatus = async () => {
    if (!thread || !activeId) return;
    const next = thread.status === 'open' ? 'closed' : 'open';
    try {
      await setSupportThreadStatus(activeId, next);
      await fetchMessages(activeId);
      fetchThreads();
    } catch { /* ignore */ }
  };

  // 会社フィルター（過去の履歴を会社ごとに確認する用途）
  const companies = Array.from(
    new Map(threads.map(t => [t.company_id, t.company_name ?? t.company_id])).entries()
  ).sort((a, b) => (a[1] ?? '').localeCompare(b[1] ?? ''));
  const filtered = threads
    .filter(t => filter === 'all' ? true : t.status === filter)
    .filter(t => companyFilter === 'all' ? true : t.company_id === companyFilter);
  const openCount = threads.filter(t => t.status === 'open').length;

  return (
    <div style={{ padding: 24, height: 'calc(100vh - 0px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexShrink: 0 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LifeBuoy size={17} color="#fff" />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>顧客対応</h1>
          <div style={{ fontSize: 11.5, color: '#9ca3af' }}>各社管理者からの問い合わせ（対応中 {openCount} 件）</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, flex: 1, minHeight: 0 }}>
        {/* ── 左: 問い合わせ一覧 ── */}
        <div style={{ width: 320, flexShrink: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: 10, borderBottom: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {([['open', '対応中'], ['closed', '完了'], ['all', 'すべて']] as const).map(([key, label]) => (
                <button key={key} onClick={() => setFilter(key)}
                  style={{
                    flex: 1, padding: '6px 0', borderRadius: 7, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700,
                    background: filter === key ? '#1d4ed8' : '#f3f4f6',
                    color: filter === key ? '#fff' : '#6b7280',
                  }}>{label}</button>
              ))}
            </div>
            {/* 会社ごとの履歴確認用フィルター */}
            <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #e5e7eb', fontSize: 12.5, color: '#374151', background: '#fff', outline: 'none', cursor: 'pointer' }}>
              <option value="all">🏢 すべての会社</option>
              {companies.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12.5, padding: 28 }}>問い合わせはありません</div>
            )}
            {filtered.map(t => (
              <button key={t.id} onClick={() => { setActiveId(t.id); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '11px 13px',
                  border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                  background: activeId === t.id ? '#eff6ff' : '#fff',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <Building2 size={12} color="#9ca3af" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.company_name ?? t.company_id}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: '#c0c5cd', flexShrink: 0 }}>{fmt(t.updated_at)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 800, borderRadius: 5, padding: '1px 6px', flexShrink: 0,
                    background: t.status === 'open' ? '#dbeafe' : '#f3f4f6',
                    color: t.status === 'open' ? '#1d4ed8' : '#9ca3af',
                  }}>{t.status === 'open' ? '対応中' : '完了'}</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>
                  {t.unread_count > 0 && (
                    <span style={{ background: '#ef4444', color: '#fff', borderRadius: 8, minWidth: 17, height: 17, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, padding: '0 5px', flexShrink: 0 }}>{t.unread_count}</span>
                  )}
                </div>
                {t.last_message && (
                  <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.last_message}</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── 右: チャット ── */}
        <div style={{ flex: 1, minWidth: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!activeId && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#c0c5cd', gap: 10 }}>
              <LifeBuoy size={40} strokeWidth={1.2} />
              <div style={{ fontSize: 13 }}>左の一覧から問い合わせを選択してください</div>
            </div>
          )}
          {activeId && thread && (
            <>
              {/* ヘッダー */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{thread.subject}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                    {thread.company_name ?? thread.company_id} ／ 起票: {thread.created_by_name ?? '-'} ／ {fmt(thread.created_at)}
                  </div>
                </div>
                <button onClick={toggleStatus}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px',
                    borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, flexShrink: 0,
                    background: thread.status === 'open' ? '#dcfce7' : '#f3f4f6',
                    color: thread.status === 'open' ? '#15803d' : '#6b7280',
                  }}>
                  {thread.status === 'open' ? <><CheckCircle2 size={13} /> 対応完了にする</> : <><RotateCcw size={13} /> 再開する</>}
                </button>
              </div>

              {/* メッセージ */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, background: '#f9fafb' }}>
                {messages.map(m => m.sender_type === 'system' ? (
                  <div key={m.id} style={{ textAlign: 'center', fontSize: 11.5, color: '#9ca3af', padding: '2px 8px' }}>
                    ── {m.body} ──
                  </div>
                ) : (
                  <div key={m.id} style={{ display: 'flex', justifyContent: m.sender_type === 'super' ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '72%' }}>
                      <div style={{ fontSize: 10.5, color: '#9ca3af', marginBottom: 2, fontWeight: 600, textAlign: m.sender_type === 'super' ? 'right' : 'left' }}>
                        {m.sender_type === 'super' ? `${m.sender_name ?? ''}（サポート）` : `${m.sender_name ?? ''}（${thread.company_name ?? '顧客'}）`}
                      </div>
                      <div style={{
                        background: m.sender_type === 'super' ? '#1d4ed8' : '#fff',
                        color: m.sender_type === 'super' ? '#fff' : '#374151',
                        border: m.sender_type === 'super' ? 'none' : '1px solid #e5e7eb',
                        borderRadius: m.sender_type === 'super' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                        padding: '9px 13px', fontSize: 13.5, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {m.body}
                        {m.attachment_path && (
                          <AttachmentView messageId={m.id} name={m.attachment_name ?? 'ファイル'} mime={m.attachment_mime ?? ''} />
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: '#c0c5cd', marginTop: 2, textAlign: m.sender_type === 'super' ? 'right' : 'left' }}>{fmt(m.created_at)}</div>
                    </div>
                  </div>
                ))}
                {peerTyping && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', padding: '2px 4px' }}>
                    <span style={{ display: 'inline-flex', gap: 2 }}>
                      <span className="sa-typing-dot" /><span className="sa-typing-dot" style={{ animationDelay: '0.15s' }} /><span className="sa-typing-dot" style={{ animationDelay: '0.3s' }} />
                    </span>
                    {thread.company_name ?? '顧客'}の担当者が入力中...
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* 入力 */}
              <div style={{ padding: '12px 14px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, flexShrink: 0, alignItems: 'flex-end' }}>
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadFile(f); }} />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  title="画像・PDFを添付（10MBまで）"
                  style={{
                    width: 40, height: 40, borderRadius: 10, background: '#f3f4f6',
                    border: 'none', cursor: sending ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                  <Paperclip size={16} color="#6b7280" />
                </button>
                <textarea
                  value={input}
                  onChange={e => { setInput(e.target.value); if (e.target.value && activeId) signalTyping(activeId); }}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }}
                  placeholder="返信を入力...（Ctrl+Enterで送信）"
                  rows={2}
                  style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', resize: 'none', fontFamily: 'inherit', minWidth: 0 }}
                  onFocus={e => e.target.style.borderColor = '#1d4ed8'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
                <button onClick={send} disabled={!input.trim() || sending}
                  style={{
                    width: 44, height: 40, borderRadius: 10, border: 'none',
                    background: input.trim() && !sending ? '#1d4ed8' : '#e5e7eb',
                    cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                  <Send size={16} color={input.trim() && !sending ? '#fff' : '#9ca3af'} />
                </button>
              </div>
              <style>{`
                .sa-typing-dot {
                  width: 5px; height: 5px; border-radius: 50%; background: #9ca3af;
                  display: inline-block; animation: saTypingBounce 1s infinite;
                }
                @keyframes saTypingBounce {
                  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
                  30% { transform: translateY(-3px); opacity: 1; }
                }
              `}</style>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
